<script setup>
import { ref, useTemplateRef, onMounted, onUnmounted, computed } from 'vue';
import { debounce } from '@chatwoot/utils';
import RecentSearches from './RecentSearches.vue';

const props = defineProps({
  modelValue: {
    type: String,
    default: '',
  },
});
const emit = defineEmits(['search', 'selectRecentSearch', 'update:modelValue']);

const searchQuery = computed({
  get: () => props.modelValue,
  set: value => emit('update:modelValue', value),
});
const isInputFocused = ref(false);
const showRecentSearches = ref(false);
const searchInput = useTemplateRef('searchInput');
const recentSearchesRef = useTemplateRef('recentSearchesRef');

const handler = e => {
  if (e.key === '/' && document.activeElement.tagName !== 'INPUT') {
    e.preventDefault();
    searchInput.value.focus();
  } else if (e.key === 'Escape' && document.activeElement.tagName === 'INPUT') {
    e.preventDefault();
    searchInput.value.blur();
  }
};

const debouncedEmit = debounce(
  value =>
    emit('search', value.length > 1 || value.match(/^[0-9]+$/) ? value : ''),
  500
);

const onInput = () => {
  debouncedEmit(searchQuery.value);

  if (searchQuery.value.trim()) {
    showRecentSearches.value = false;
  } else if (isInputFocused.value) {
    showRecentSearches.value = true;
  }
};

const onFocus = () => {
  isInputFocused.value = true;
  if (!searchQuery.value.trim()) {
    showRecentSearches.value = true;
  }
};

const onBlur = () => {
  isInputFocused.value = false;
  showRecentSearches.value = false;
};

const onSelectRecentSearch = query => {
  searchQuery.value = query;
  emit('selectRecentSearch', query);
  showRecentSearches.value = false;
  searchInput.value.focus();
};

const addToRecentSearches = query => {
  if (recentSearchesRef.value) {
    recentSearchesRef.value.addRecentSearch(query);
  }
};

defineExpose({
  addToRecentSearches,
});

onMounted(() => {
  searchInput.value.focus();
  document.addEventListener('keydown', handler);
});

onUnmounted(() => {
  document.removeEventListener('keydown', handler);
});
</script>

<template>
  <div
    class="rounded-2xl transition-all duration-300 ease-in-out relative flex items-start flex-col border bg-background shadow-xl ring-1 ring-border/50 divide-y divide-border"
    :class="{
      'border-primary ring-4 ring-primary/10': isInputFocused,
      'border-border': !isInputFocused,
    }"
  >
    <div class="flex items-center w-full h-16 px-5 gap-3">
      <div class="flex items-center">
        <span
          class="i-lucide-search size-5 transition-colors duration-300"
          :class="{
            'text-primary': isInputFocused,
            'text-muted-foreground': !isInputFocused,
          }"
        />
      </div>
      <input
        ref="searchInput"
        v-model="searchQuery"
        type="search"
        class="reset-base outline-none w-full m-0 bg-transparent border-transparent shadow-none text-foreground placeholder:text-muted-foreground text-lg font-medium"
        :placeholder="$t('SEARCH.INPUT_PLACEHOLDER')"
        @focus="onFocus"
        @blur="onBlur"
        @input="onInput"
      />
      <div
        class="flex items-center gap-1.5 px-2 py-1 rounded-lg bg-muted border border-border/50 flex-shrink-0"
      >
        <kbd class="text-[10px] font-bold text-muted-foreground leading-none">
          {{ $t('GENERAL.ESC') || 'ESC' }}
        </kbd>
        <span class="text-[10px] text-muted-foreground/60">
          {{ $t('SEARCH.PRESS_ESC_TO_CLOSE') }}
        </span>
      </div>
    </div>

    <slot />

    <div
      class="transition-all duration-200 ease-out grid overflow-hidden w-full !border-t-0"
      :class="
        showRecentSearches
          ? 'grid-rows-[1fr] opacity-100'
          : 'grid-rows-[0fr] opacity-0'
      "
    >
      <div class="overflow-hidden w-full">
        <RecentSearches
          ref="recentSearchesRef"
          @select-search="onSelectRecentSearch"
          @clear-all="showRecentSearches = false"
        />
      </div>
    </div>
  </div>
</template>
